import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import { formatNumber } from "../../Funcoes/formatNumber";
import DataTable from "../../Componentes/Utils/DataTable";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import { useModal } from "../../contexts/ModalContext";
import ModalFiltersControladoria from "./ModalFiltersControladoria";
import ModalParcelasFluxoCaixa from "./ModalParcelasFluxoCaixa";
import { useToast } from "../../contexts/ToastContext";
import { useRegisterControladoriaPageActions } from "./layout";
import DesembolsoChart from "./dashboard/DesembolsoChart";

const API_URL = "Painel/API/Controladoria/PedidosCompra/FluxoCaixa/index.php";

// Cor por status de consistência. ALERTA é informativo (azul), distinto de
// SEM CONDICAO (âmbar) e DIVERGENCIA (vermelho).
const statusColor = (status) =>
    status === 'OK' ? '#2e7d52'
        : status === 'ALERTA' ? '#1d6fb8'
            : status === 'SEM CONDICAO' ? '#b45309'
                : '#b42318';
const statusBg = (status) =>
    status === 'OK' ? 'rgba(46, 125, 82, 0.14)'
        : status === 'ALERTA' ? 'rgba(29, 111, 184, 0.14)'
            : status === 'SEM CONDICAO' ? 'rgba(180, 83, 9, 0.14)'
                : 'rgba(180, 35, 24, 0.14)';

// Cor por status de entrega do pedido.
const entregaColor = (cod) =>
    cod === 'ENTREGUE_TOTAL' ? '#2e7d52'
        : cod === 'ENTREGUE_PARCIAL' ? '#b45309'
            : '#64748b';
const entregaBg = (cod) =>
    cod === 'ENTREGUE_TOTAL' ? 'rgba(46, 125, 82, 0.14)'
        : cod === 'ENTREGUE_PARCIAL' ? 'rgba(180, 83, 9, 0.14)'
            : 'rgba(100, 116, 139, 0.14)';

const GRAN_CONFIG = {
    mes: { key: 'por_mes', labelKey: 'mes_fmt', titulo: 'Mês' },
    semana: { key: 'por_semana', labelKey: 'label', titulo: 'Semana' },
    dia: { key: 'por_dia', labelKey: 'data_fmt', titulo: 'Dia' },
};

// Verifica se o pedido tem alguma parcela cujo vencimento cai no período selecionado.
const pedidoNoPeriodo = (pedido, gran, row) => {
    const parcelas = pedido.parcelas || [];
    return parcelas.some((parc) => {
        const d = parc.data_prevista;
        if (!d) return false;
        if (gran === 'mes') return d.slice(0, 7) === row.mes;
        if (gran === 'dia') return d === row.data;
        if (gran === 'semana') return d >= row.semana_inicio && d <= row.semana_fim;
        return false;
    });
};

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`
const BodyWrapper = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding: 12px;
    gap: 12px;
    box-sizing: border-box;

    @media (min-width: 900px) {
        padding: 15px;
        gap: 15px;
    }
`
const KpiGrid = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    flex-shrink: 0;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    @media (max-width: 600px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`
const KpiCard = styled.div`
    background-color: var(--ctrl-surface, #fff);
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    border-radius: 8px;
    padding: 12px 15px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`
const KpiLabel = styled.span`
    font-size: 12px;
    color: var(--ctrl-text-muted, #555);
`
const KpiValue = styled.span`
    font-size: 18px;
    font-weight: bold;
    color: var(--ctrl-text, #333);
`
const Panel = styled.div`
    background-color: var(--ctrl-surface, #fff);
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    border-radius: 8px;
    padding: 15px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`
const PanelTitle = styled.h2`
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--ctrl-text-muted, #555);
`
const ChartPanel = styled(Panel)`
    width: 100%;
    flex-shrink: 0;
`
const ChartPanelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;

    & h2 {
        margin: 0;
    }
`
const ChartControls = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`
const ChartSelect = styled.select`
    height: 28px;
    padding: 0 6px;
    font-size: 12px;
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    border-radius: 4px;
    background-color: var(--ctrl-surface, #fff);
    color: var(--ctrl-text, #333);
    cursor: pointer;
`
const ChartCheck = styled.label`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--ctrl-text-muted, #555);
    cursor: pointer;
`
const ChartHint = styled.p`
    margin: 8px 0 0;
    font-size: 12px;
    color: #999;
`
const StatusBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: ${({ status }) => statusColor(status)};
    background-color: ${({ status }) => statusBg(status)};
`
const EntregaBadge = styled.span`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ cod }) => entregaColor(cod)};
    background-color: ${({ cod }) => entregaBg(cod)};
`
const TabsBar = styled.div`
    width: 100%;
    display: flex;
    gap: 4px;
    border-bottom: 1.2px solid var(--ctrl-border, #d3d3d3);
    flex-shrink: 0;
`
const TabButton = styled.button`
    padding: 8px 16px;
    border: none;
    background-color: transparent;
    font-size: 14px;
    cursor: pointer;
    color: ${({ active }) => (active ? '#4a6fa5' : 'var(--ctrl-text-muted, #555)')};
    border-bottom: 2px solid ${({ active }) => (active ? '#4a6fa5' : 'transparent')};
    font-weight: ${({ active }) => (active ? 700 : 400)};

    &:hover {
        color: #4a6fa5;
    }
`
const FilterChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    padding: 6px 12px;
    background-color: rgba(74, 111, 165, 0.10);
    border: 1.2px solid #4a6fa5;
    border-radius: 999px;
    font-size: 13px;
    color: #33475b;

    & b {
        color: var(--ctrl-text, #1f2937);
    }
`
const ClearButton = styled.button`
    border: none;
    background-color: transparent;
    color: #4a6fa5;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: #b42318;
    }
`
const ActiveFiltersBar = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
`
const ActiveFiltersLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: var(--ctrl-text-muted, #555);
`
const ClearAllButton = styled.button`
    margin-left: auto;
    border: none;
    background-color: transparent;
    color: #b42318;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;

    &:hover {
        text-decoration: underline;
    }
`
const TableWrapper = styled.div`
    width: 100%;
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    border-radius: 8px;
    overflow: auto;
    max-height: 45dvh;
`
const PedidosTableWrapper = styled(TableWrapper)`
    display: none;

    @media (min-width: 900px) {
        display: block;
    }
`
const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 900px) {
        display: none;
    }
`
const MobileCard = styled.button`
    width: 100%;
    text-align: left;
    border: 1px solid var(--ctrl-border, #d3d3d3);
    background: var(--ctrl-surface, #fff);
    border-radius: 10px;
    padding: 12px 14px;
    cursor: pointer;
    color: var(--ctrl-text, #333);
    font: inherit;

    &:active {
        transform: scale(0.99);
    }
`
const MobileCardTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 4px;
`
const MobileCardMeta = styled.div`
    font-size: 12px;
    color: var(--ctrl-text-muted, #667788);
    line-height: 1.45;
`
const Coluna = styled.td`
    text-align: ${({ align }) => align || 'left'};
`
const CondCodigo = styled.div`
    font-weight: 600;
`
const FornecedorCod = styled.div`
    font-size: 12px;
    color: #777;
    margin-top: 2px;
`
const RecorrenteBadge = styled.span`
    display: inline-block;
    margin-top: 4px;
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    color: #5b3d8a;
    background-color: rgba(124, 82, 179, 0.14);
`
const CondDesc = styled.div`
    font-size: 12px;
    color: #777;
`
const CondDiverge = styled.div`
    font-size: 12px;
    color: #1d6fb8;
    margin-top: 2px;
`
const CondBase = styled.div`
    font-size: 11px;
    color: #999;
    margin-top: 2px;
`
const LinhaPedido = styled.tr`
    cursor: pointer;

    &:hover {
        background-color: #f0f4fa;
    }
`
const LoadingWrapper = styled.div`
    width: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
`

const RESUMO_INICIAL = {
    total_pedidos: 0,
    total_alertas: 0,
    total_30_dias: 0,
    total_60_dias: 0,
    total_90_dias: 0,
    total_geral_aberto: 0,
    curva_desembolso_mes: [],
    consistencia: { ok: 0, alerta: 0, sem_condicao: 0, divergencia: 0 }
};

const nomeOpcao = (options, cod) => {
    const opcao = (options || []).find((o) => String(o.cod) === String(cod));
    return opcao ? opcao.nome : cod;
};

const dataHoje = () => new Date().toISOString().slice(0, 10);
const primeiroDiaMes = () => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
};

const FILTROS_STORAGE_KEY = 'fluxoCaixa:filtros';

const filtrosPadrao = () => ({
    filial: '',
    comprador: [],
    grupo: [],
    solicitante: [],
    centroCusto: [],
    itemConta: [],
    dataInicio: primeiroDiaMes(),
    dataFim: dataHoje(),
    somenteAbertos: true
});

// Recupera os filtros salvos (mesclados com os padrões, para tolerar versões
// antigas do formato) ou os padrões quando não há nada persistido.
const carregarFiltros = () => {
    try {
        const raw = localStorage.getItem(FILTROS_STORAGE_KEY);
        if (!raw) return filtrosPadrao();
        return { ...filtrosPadrao(), ...JSON.parse(raw) };
    } catch {
        return filtrosPadrao();
    }
};

const persistirFiltros = (f) => {
    try {
        localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(f));
    } catch {
        /* ignora indisponibilidade do localStorage */
    }
};

// Mapeamento entre chaves do estado (camelCase) e nomes de parâmetro na URL.
const FILTROS_LISTA_URL = [
    { chave: 'comprador', param: 'comprador' },
    { chave: 'grupo', param: 'grupo' },
    { chave: 'solicitante', param: 'solicitante' },
    { chave: 'centroCusto', param: 'centro_custo' },
    { chave: 'itemConta', param: 'item_conta' },
];

// Lê os filtros da query string; retorna null quando não há nenhum filtro na
// URL (para então cair no localStorage/padrões).
const filtrosDaUrl = (searchParams) => {
    const chavesRelevantes = ['filial', 'data_inicio', 'data_fim', 'todos', ...FILTROS_LISTA_URL.map((f) => f.param)];
    if (!chavesRelevantes.some((k) => searchParams.has(k))) return null;

    const padrao = filtrosPadrao();
    const filtros = {
        ...padrao,
        filial: searchParams.get('filial') || '',
        dataInicio: searchParams.get('data_inicio') || padrao.dataInicio,
        dataFim: searchParams.get('data_fim') || padrao.dataFim,
        somenteAbertos: searchParams.get('todos') !== '1',
    };
    FILTROS_LISTA_URL.forEach(({ chave, param }) => {
        filtros[chave] = searchParams.getAll(param).filter(Boolean);
    });
    return filtros;
};

// Serializa os filtros para uma URLSearchParams (para compartilhar a visão).
const filtrosParaUrl = (f) => {
    const sp = new URLSearchParams();
    if (f.filial) sp.set('filial', f.filial);
    FILTROS_LISTA_URL.forEach(({ chave, param }) => {
        (f[chave] || []).forEach((v) => {
            if (v !== '' && v != null) sp.append(param, v);
        });
    });
    if (f.dataInicio) sp.set('data_inicio', f.dataInicio);
    if (f.dataFim) sp.set('data_fim', f.dataFim);
    if (!f.somenteAbertos) sp.set('todos', '1');
    return sp;
};

const FluxoCaixa = () => {

    const api = useApi();
    const { notifyError } = useToast();
    const { isModalOpen, openModal, closeModal } = useModal();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [resumo, setResumo] = useState(RESUMO_INICIAL);
    const [pedidos, setPedidos] = useState([]);
    const [consolidacao, setConsolidacao] = useState({ por_dia: [], por_semana: [], por_mes: [] });
    const [compradores, setCompradores] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [solicitantes, setSolicitantes] = useState([]);
    const [centrosCusto, setCentrosCusto] = useState([]);
    const [itensConta, setItensConta] = useState([]);

    const [abaAtiva, setAbaAtiva] = useState('pedidos');
    const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

    const [chartGran, setChartGran] = useState('mes');
    const [chartTipo, setChartTipo] = useState('bar');
    const [chartAcum, setChartAcum] = useState(false);
    const [filtroPeriodo, setFiltroPeriodo] = useState(null);

    // Prioridade dos filtros iniciais: URL (compartilhável) > localStorage > padrão.
    const [filtros, setFiltros] = useState(() => filtrosDaUrl(searchParams) || carregarFiltros());

    const fetchDados = async (curFiltros = filtros) => {
        setLoading(true);
        setFiltroPeriodo(null);

        try {

            const params = new URLSearchParams();

            // Envia filtros de seleção múltipla como `chave[]=valor` (suporta
            // valores com vírgula, ex.: nomes de solicitantes).
            const appendLista = (chave, valores) => {
                (valores || []).forEach((v) => {
                    if (v !== '' && v != null) params.append(`${chave}[]`, v);
                });
            };

            if (curFiltros.filial) params.append('filial', curFiltros.filial);
            appendLista('comprador', curFiltros.comprador);
            appendLista('grupo', curFiltros.grupo);
            appendLista('solicitante', curFiltros.solicitante);
            appendLista('centro_custo', curFiltros.centroCusto);
            appendLista('item_conta', curFiltros.itemConta);
            if (curFiltros.dataInicio) params.append('data_inicio', curFiltros.dataInicio);
            if (curFiltros.dataFim) params.append('data_fim', curFiltros.dataFim);
            if (!curFiltros.somenteAbertos) params.append('todos', '1');

            const { data } = await api.get(`${API_URL}?${params.toString()}`);

            setResumo(data.resumo_executivo || RESUMO_INICIAL);
            setPedidos(data.pedidos || []);
            setConsolidacao(data.consolidacao || { por_dia: [], por_semana: [], por_mes: [] });
            setCompradores(data.compradores || []);
            setGrupos(data.grupos || []);
            setSolicitantes(data.solicitantes || []);
            setCentrosCusto(data.centros_custo || []);
            setItensConta(data.itens_conta || []);

        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao carregar o fluxo de caixa.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        // Reflete na URL os filtros iniciais (caso venham do localStorage/padrão),
        // para que a visão já seja compartilhável ao carregar a tela.
        setSearchParams(filtrosParaUrl(filtros), { replace: true });
        fetchDados(filtros);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshData = async () => {

        if (refreshing) return;

        setRefreshing(true);
        await fetchDados();
        setRefreshing(false);

    }

    // Aplica um conjunto de filtros: atualiza o estado, persiste (localStorage +
    // URL) e recarrega.
    const aplicarERecarregar = (novosFiltros) => {
        setFiltros(novosFiltros);
        persistirFiltros(novosFiltros);
        setSearchParams(filtrosParaUrl(novosFiltros), { replace: true });
        fetchDados(novosFiltros);
    }

    const aplicarFiltros = () => {
        aplicarERecarregar(filtros);
        closeModal('fluxo-caixa-filtros');
    }

    const removerFiltro = (chave) => {
        const vazio = Array.isArray(filtros[chave]) ? [] : '';
        aplicarERecarregar({ ...filtros, [chave]: vazio });
    }

    const limparFiltros = () => {
        aplicarERecarregar(filtrosPadrao());
    }

    const OpenFilters = () => {

        openModal('fluxo-caixa-filtros');

    }

    useRegisterControladoriaPageActions({
        onRefresh: refreshData,
        onFilter: OpenFilters,
        refreshing,
    });

    const abrirParcelas = (pedido) => {
        setPedidoSelecionado(pedido);
        openModal('fluxo-caixa-parcelas');
    }

    const fecharParcelas = () => {
        closeModal('fluxo-caixa-parcelas');
        setPedidoSelecionado(null);
    }

    const chartRows = useMemo(
        () => consolidacao[GRAN_CONFIG[chartGran].key] || [],
        [consolidacao, chartGran]
    );

    const pedidosFiltrados = useMemo(() => {
        if (!filtroPeriodo) return pedidos;
        return pedidos.filter((p) => pedidoNoPeriodo(p, filtroPeriodo.gran, filtroPeriodo.row));
    }, [pedidos, filtroPeriodo]);

    // Chips dos filtros ativos (exceto período/datas, já tratados à parte).
    const chipsFiltrosAtivos = useMemo(() => {
        const definicoes = [
            { chave: 'comprador', label: 'Comprador', options: compradores },
            { chave: 'solicitante', label: 'Solicitante', options: solicitantes },
            { chave: 'centroCusto', label: 'Centro de custo', options: centrosCusto },
            { chave: 'itemConta', label: 'Conta contábil', options: itensConta },
            { chave: 'grupo', label: 'Grupo', options: grupos },
        ];

        const chips = [];

        if (filtros.filial) {
            chips.push({ chave: 'filial', label: 'Filial', texto: filtros.filial });
        }

        definicoes.forEach(({ chave, label, options }) => {
            const valores = filtros[chave] || [];
            if (valores.length === 0) return;
            const texto = valores.length === 1
                ? nomeOpcao(options, valores[0])
                : `${valores.length} selecionados`;
            chips.push({ chave, label, texto });
        });

        return chips;
    }, [filtros, compradores, solicitantes, centrosCusto, itensConta, grupos]);

    const selecionarPeriodo = (row) => {
        const cfg = GRAN_CONFIG[chartGran];
        setFiltroPeriodo({ gran: chartGran, row, label: row[cfg.labelKey] });
        setAbaAtiva('pedidos');
    };

    const renderTabelaConsolidada = (rows, labelKey, aba) => (
        <TableWrapper>
            <DataTable>
                <thead>
                    <tr>
                        <th>{aba === 'dia' ? 'DATA' : aba === 'semana' ? 'SEMANA' : 'MÊS'}</th>
                        <th>VALOR PREVISTO</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={2}>Nenhum registro encontrado.</td></tr>
                    ) : (
                        rows.map((r, index) => (
                            <tr key={index}>
                                <td>{r[labelKey]}</td>
                                <Coluna align="right">{`R$ ${formatCurrency(r.valor)}`}</Coluna>
                            </tr>
                        ))
                    )}
                </tbody>
            </DataTable>
        </TableWrapper>
    );

    return (
        <>
            <Container>
                <BodyWrapper>
                    {loading ? (
                        <LoadingWrapper>
                            <SmallLoading text="Carregando relatório..." />
                        </LoadingWrapper>
                    ) : (
                        <>
                            <KpiGrid>
                                <KpiCard>
                                    <KpiLabel>Próximos 30 dias</KpiLabel>
                                    <KpiValue>R$ {formatCurrency(resumo.total_30_dias)}</KpiValue>
                                </KpiCard>
                                <KpiCard>
                                    <KpiLabel>Próximos 60 dias</KpiLabel>
                                    <KpiValue>R$ {formatCurrency(resumo.total_60_dias)}</KpiValue>
                                </KpiCard>
                                <KpiCard>
                                    <KpiLabel>Próximos 90 dias</KpiLabel>
                                    <KpiValue>R$ {formatCurrency(resumo.total_90_dias)}</KpiValue>
                                </KpiCard>
                                <KpiCard>
                                    <KpiLabel>Total em aberto</KpiLabel>
                                    <KpiValue>R$ {formatCurrency(resumo.total_geral_aberto)}</KpiValue>
                                </KpiCard>
                                <KpiCard>
                                    <KpiLabel>Pedidos</KpiLabel>
                                    <KpiValue>{formatNumber(resumo.total_pedidos)}</KpiValue>
                                </KpiCard>
                                <KpiCard>
                                    <KpiLabel>Alertas</KpiLabel>
                                    <KpiValue>{formatNumber(resumo.total_alertas)}</KpiValue>
                                </KpiCard>
                            </KpiGrid>

                            {chipsFiltrosAtivos.length > 0 && (
                                <ActiveFiltersBar>
                                    <ActiveFiltersLabel>Filtros ativos:</ActiveFiltersLabel>
                                    {chipsFiltrosAtivos.map((chip) => (
                                        <FilterChip key={chip.chave}>
                                            {chip.label}: <b>{chip.texto}</b>
                                            <ClearButton onClick={() => removerFiltro(chip.chave)} title="Remover filtro">✕</ClearButton>
                                        </FilterChip>
                                    ))}
                                    <ClearAllButton onClick={limparFiltros}>Limpar filtros</ClearAllButton>
                                </ActiveFiltersBar>
                            )}

                            <ChartPanel>
                                <ChartPanelHeader>
                                    <PanelTitle>Curva de desembolso previsto</PanelTitle>
                                    <ChartControls>
                                        <ChartSelect value={chartGran} onChange={(e) => setChartGran(e.target.value)} title="Granularidade">
                                            <option value="mes">Mês</option>
                                            <option value="semana">Semana</option>
                                            <option value="dia">Dia</option>
                                        </ChartSelect>
                                        <ChartSelect value={chartTipo} onChange={(e) => setChartTipo(e.target.value)} title="Tipo de gráfico">
                                            <option value="bar">Barras</option>
                                            <option value="line">Linha</option>
                                        </ChartSelect>
                                        <ChartCheck>
                                            <input type="checkbox" checked={chartAcum} onChange={(e) => setChartAcum(e.target.checked)} />
                                            Acumulado
                                        </ChartCheck>
                                    </ChartControls>
                                </ChartPanelHeader>
                                <DesembolsoChart
                                    rows={chartRows}
                                    labelKey={GRAN_CONFIG[chartGran].labelKey}
                                    chartTipo={chartTipo}
                                    chartAcum={chartAcum}
                                    onSelectPeriodo={selecionarPeriodo}
                                />
                                <ChartHint>Clique em uma barra/ponto para filtrar os pedidos daquele período.</ChartHint>
                            </ChartPanel>

                            <TabsBar>
                                <TabButton active={abaAtiva === 'pedidos'} onClick={() => setAbaAtiva('pedidos')}>Pedidos</TabButton>
                                <TabButton active={abaAtiva === 'dia'} onClick={() => setAbaAtiva('dia')}>Por dia</TabButton>
                                <TabButton active={abaAtiva === 'semana'} onClick={() => setAbaAtiva('semana')}>Por semana</TabButton>
                                <TabButton active={abaAtiva === 'mes'} onClick={() => setAbaAtiva('mes')}>Por mês</TabButton>
                            </TabsBar>

                            {abaAtiva === 'pedidos' && (
                                <>
                                    {filtroPeriodo && (
                                        <FilterChip>
                                            Período: <b>{filtroPeriodo.label}</b> ({GRAN_CONFIG[filtroPeriodo.gran].titulo})
                                            <ClearButton onClick={() => setFiltroPeriodo(null)}>limpar ✕</ClearButton>
                                        </FilterChip>
                                    )}
                                    <PedidosTableWrapper>
                                        <DataTable>
                                            <thead>
                                                <tr>
                                                    <th>FILIAL</th>
                                                    <th>PEDIDO</th>
                                                    <th>FORNECEDOR</th>
                                                    <th>SOLICITANTE</th>
                                                    <th>COND. PAGTO</th>
                                                    <th>FORMA PAGTO</th>
                                                    <th>EMISSÃO</th>
                                                    <th>PREV. ENTREGA</th>
                                                    <th>ENTREGA</th>
                                                    <th>VALOR</th>
                                                    <th>PARCELAS</th>
                                                    <th>ÚLTIMO PAGTO.</th>
                                                    <th>STATUS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pedidosFiltrados.length === 0 ? (
                                                    <tr><td colSpan={13}>Nenhum pedido encontrado.</td></tr>
                                                ) : (
                                                    pedidosFiltrados.map((p, index) => {
                                                        const divergeCond = p.cond_fornecedor && p.cond_pagto && p.cond_fornecedor !== p.cond_pagto;
                                                        return (
                                                            <LinhaPedido
                                                                key={`${p.filial}-${p.num_pedido}-${index}`}
                                                                onClick={() => abrirParcelas(p)}
                                                            >
                                                            <td>{p.filial || '—'}</td>
                                                            <td>
                                                                <div>{p.num_pedido}</div>
                                                                {p.recorrente ? <RecorrenteBadge>Recorrente</RecorrenteBadge> : null}
                                                            </td>
                                                            <td>
                                                                <div>{p.fornecedor || '—'}</div>
                                                                {p.cod_fornecedor ? (
                                                                    <FornecedorCod>
                                                                        Cód: {p.cod_fornecedor}
                                                                        {p.loja_fornecedor ? `/${p.loja_fornecedor}` : ''}
                                                                    </FornecedorCod>
                                                                ) : null}
                                                            </td>
                                                            <td>{p.solicitante || 'Não informado'}</td>
                                                            <td>
                                                                    <CondCodigo>{p.cond_pagto || '—'}</CondCodigo>
                                                                    {p.desc_cond_pagto ? <CondDesc>{p.desc_cond_pagto}</CondDesc> : null}
                                                                    {divergeCond ? (
                                                                        <CondDiverge title="Condição padrão do fornecedor (A2_COND)">
                                                                            Pedido <b>{p.cond_pagto}</b> × Fornecedor <b>{p.cond_fornecedor}</b>
                                                                        </CondDiverge>
                                                                    ) : null}
                                                                    {p.base_calculo_tipo_desc ? (
                                                                        <CondBase>
                                                                            {p.base_calculo_tipo_desc}
                                                                            {p.base_calculo_data ? ` (${p.base_calculo_data})` : ''}
                                                                        </CondBase>
                                                                    ) : null}
                                                                </td>
                                                                <td>{p.forma_pagto || p.forma_pagto_cod || '—'}</td>
                                                                <td>{p.dt_emissao || '—'}</td>
                                                                <td>{p.dt_entrega || '—'}</td>
                                                                <td>
                                                                    <EntregaBadge cod={p.status_entrega}>
                                                                        {p.status_entrega_label || 'Pendente'}
                                                                    </EntregaBadge>
                                                                </td>
                                                                <Coluna align="right">{`R$ ${formatCurrency(p.valor_total)}`}</Coluna>
                                                                <Coluna align="center">{formatNumber(p.qtd_parcelas)}</Coluna>
                                                                <td>{p.data_ultimo_pagamento || '—'}</td>
                                                                <td><StatusBadge status={p.status_consistencia}>{p.status_consistencia}</StatusBadge></td>
                                                            </LinhaPedido>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </DataTable>
                                    </PedidosTableWrapper>
                                    <MobileCards>
                                        {pedidosFiltrados.length === 0 ? (
                                            <MobileCard as="div" style={{ cursor: "default" }}>
                                                <MobileCardMeta>Nenhum pedido encontrado.</MobileCardMeta>
                                            </MobileCard>
                                        ) : (
                                            pedidosFiltrados.map((p, index) => (
                                                <MobileCard
                                                    key={`${p.filial}-${p.num_pedido}-m-${index}`}
                                                    type="button"
                                                    onClick={() => abrirParcelas(p)}
                                                >
                                                    <MobileCardTitle>
                                                        Pedido {p.num_pedido} · Filial {p.filial || "—"}
                                                        {p.recorrente ? " · Recorrente" : ""}
                                                    </MobileCardTitle>
                                                    <MobileCardMeta>
                                                        {p.fornecedor || "—"} · R$ {formatCurrency(p.valor_total)} ·{" "}
                                                        {p.status_entrega_label || "Pendente"} · {p.status_consistencia || "—"}
                                                    </MobileCardMeta>
                                                </MobileCard>
                                            ))
                                        )}
                                    </MobileCards>
                                </>
                            )}

                            {abaAtiva === 'dia' && renderTabelaConsolidada(consolidacao.por_dia || [], 'data_fmt', 'dia')}
                            {abaAtiva === 'semana' && renderTabelaConsolidada(consolidacao.por_semana || [], 'label', 'semana')}
                            {abaAtiva === 'mes' && renderTabelaConsolidada(consolidacao.por_mes || [], 'mes_fmt', 'mes')}
                        </>
                    )}
                </BodyWrapper>
            </Container>
            {
                isModalOpen('fluxo-caixa-filtros') && (
                    <ModalFiltersControladoria
                        fecharModal={closeModal}
                        filtros={filtros}
                        setFiltros={setFiltros}
                        aplicarFiltros={aplicarFiltros}
                        applyLabel="Gerar relatório"
                        fields={[
                            { name: "filial", label: "Filial:", type: "text", placeholder: "Ex: 01010001" },
                            { name: "comprador", label: "Comprador:", type: "multiselect", options: compradores, placeholder: "Todos os compradores" },
                            { name: "solicitante", label: "Solicitante:", type: "multiselect", options: solicitantes, placeholder: "Todos os solicitantes" },
                            { name: "centroCusto", label: "Centro de custo:", type: "multiselect", options: centrosCusto, placeholder: "Todos os centros de custo" },
                            { name: "itemConta", label: "Conta contábil (item conta):", type: "multiselect", options: itensConta, placeholder: "Todas as contas" },
                            { name: "grupo", label: "Grupo de produtos:", type: "multiselect", options: grupos, placeholder: "Todos os grupos" },
                            { name: "dataInicio", label: "Emissão de:", type: "date", id: "data-inicial" },
                            { name: "dataFim", label: "Emissão até:", type: "date", id: "data-final" },
                            { name: "somenteAbertos", label: "Somente em aberto", type: "checkbox", id: "somente-abertos" },
                        ]}
                    />
                )
            }
            {
                isModalOpen('fluxo-caixa-parcelas') && pedidoSelecionado && (
                    <ModalParcelasFluxoCaixa
                        pedido={pedidoSelecionado}
                        onClose={fecharParcelas}
                    />
                )
            }
        </>
    )
}

export default FluxoCaixa;
